import { FormattedMessage } from 'react-intl';
import { getBatteryLevelPercentage, getCargoAtStopovers, isUUID } from '../../common/tourUtils';
import { useAppSelector } from '../../configuration/setup/hooks';
import { TimelineEventType } from '../../store/facade/facadeApiV3';
import { selectOrderedWaypoints } from '../../store/search/searchSelectors';
import { selectCurrentTour } from '../../store/tour/tourSelectors';
import type { Itinerary } from '../../store/tour/tourSlice';
import { selectLocale } from '../../store/lang/langSelectors';
import { selectBatteryCapacity } from '../../store/vehicleProfile/vehicleProfileSelectors';
import { ArrivalPanel } from './Sections/ArrivalPanel';
import { type ChargingInfo, ChargingPanel } from './Sections/ChargingPanel';
import { DeparturePanel } from './Sections/DeparturePanel';
import { type DrivingInfo, DrivingPanel } from './Sections/DrivingPanel';
import { type StopoverInfo, StopoverPanel } from './Sections/StopoverPanel';

const formatAddress = (street?: string, city?: string) => {
    if (!street || !city) {
        return undefined;
    }
    return `${street}, ${city}`;
};

export type SectionsContainerProps = {
    itineraries?: Itinerary[];
    selectedItinerary?: number;
};

export const SectionsContainer = ({ itineraries, selectedItinerary }: SectionsContainerProps) => {
    const waypoints = useAppSelector(selectOrderedWaypoints);
    const batteryCapacity = useAppSelector(selectBatteryCapacity).value;
    const locale = useAppSelector(selectLocale);
    const tour = useAppSelector(selectCurrentTour);
    const currentItinerary = selectedItinerary !== undefined ? itineraries?.at(selectedItinerary) : undefined;

    const cargoAtStopovers = getCargoAtStopovers(tour?.waypoints ?? [], tour?.departure.initialCargoWeight ?? 0);
    const firstWaypoint = waypoints.at(0);
    const lastWaypoint = waypoints.at(-1);
    let stopoverCounter = 0;

    const renderTimelineEvents = () => {
        return currentItinerary?.events?.timelineEvents?.map((event, eventIndex) => {
            if (event.timelineEventType === TimelineEventType.IntermediateStop) {
                const { occurredAt, address, duration, expectedBatteryLevelAtArrival, eventId } = event;
                const stopover: StopoverInfo = {
                    locale,
                    arrivalTime: occurredAt,
                    address: formatAddress(address?.street, address?.city),
                    duration,
                    isInRange: Boolean(expectedBatteryLevelAtArrival && expectedBatteryLevelAtArrival > 0),
                    cargoAtDeparture: cargoAtStopovers.at(stopoverCounter++),
                    batteryPct: getBatteryLevelPercentage(batteryCapacity, expectedBatteryLevelAtArrival),
                };
                return <StopoverPanel {...stopover} key={`stopover-${eventId}`} />;
            }

            if (event.timelineEventType === TimelineEventType.ChargeStop) {
                const prevEvent = currentItinerary?.events?.timelineEvents?.at(eventIndex - 1);
                if (prevEvent?.timelineEventType === TimelineEventType.Driving) {
                    return null;
                }

                const {
                    stationId,
                    occurredAt,
                    maxDuration,
                    operator,
                    address,
                    expectedBatteryLevelAtArrival,
                    expectedBatteryLevelAtDeparture,
                    eventId,
                } = event;
                const charging: ChargingInfo = {
                    arrivalTime: occurredAt,
                    duration: maxDuration,
                    operatorName: operator?.name,
                    address: formatAddress(address?.street, address?.city),
                    isInRange: Boolean(expectedBatteryLevelAtArrival && expectedBatteryLevelAtArrival > 0),
                    arrivalBatteryPct: getBatteryLevelPercentage(batteryCapacity, expectedBatteryLevelAtArrival),
                    departBatteryPct: getBatteryLevelPercentage(batteryCapacity, expectedBatteryLevelAtDeparture),
                    hasTCScontract: isUUID(stationId ?? ''),
                };

                return <ChargingPanel {...charging} key={`charging-${eventId}`} />;
            }

            if (event.timelineEventType === TimelineEventType.Transit) {
                const { occurredAt, sectionId, eventId } = event;
                if (sectionId === undefined) {
                    return null;
                }

                const section = currentItinerary?.sections?.find(it => it.sectionId === sectionId);
                if (!section) {
                    return null;
                }

                const nextSection = currentItinerary?.sections?.find(it => it.sectionId === sectionId + 1);
                const transit: DrivingInfo = {
                    locale,
                    departure: occurredAt,
                    duration: section.duration,
                    distance: section.distance,
                    type: section.type as unknown as TimelineEventType,
                    batteryPct: getBatteryLevelPercentage(batteryCapacity, nextSection?.evBatteryCapacityDeparture),
                };
                return <DrivingPanel driving={transit} key={`transit-${eventId}`} />;
            }

            if (event.timelineEventType === TimelineEventType.Driving) {
                const { occurredAt, sectionId, eventId } = event;
                const section = currentItinerary?.sections?.find(it => it.sectionId === sectionId);
                if (!section) {
                    return null;
                }

                const driving: DrivingInfo = {
                    locale,
                    departure: occurredAt,
                    duration: section.duration,
                    distance: section.distance,
                    type: section.type as unknown as TimelineEventType,
                };

                const nextEvent = currentItinerary?.events?.timelineEvents?.at(eventIndex + 1);
                const panels = [<DrivingPanel driving={driving} key={`driving-${eventId}`} />];

                if (nextEvent?.timelineEventType === TimelineEventType.ChargeStop) {
                    const {
                        stationId,
                        occurredAt: chargingOccurredAt,
                        maxDuration,
                        operator,
                        address,
                        expectedBatteryLevelAtArrival,
                        expectedBatteryLevelAtDeparture,
                        eventId: chargingEventId,
                    } = nextEvent;
                    const charging: ChargingInfo = {
                        arrivalTime: chargingOccurredAt,
                        duration: maxDuration,
                        operatorName: operator?.name,
                        address: formatAddress(address?.street, address?.city),
                        isInRange: Boolean(expectedBatteryLevelAtArrival && expectedBatteryLevelAtArrival > 0),
                        arrivalBatteryPct: getBatteryLevelPercentage(batteryCapacity, expectedBatteryLevelAtArrival),
                        departBatteryPct: getBatteryLevelPercentage(batteryCapacity, expectedBatteryLevelAtDeparture),
                        hasTCScontract: isUUID(stationId ?? ''),
                    };
                    panels.push(<ChargingPanel {...charging} key={`charging-after-driving-${chargingEventId}`} />);
                }

                return panels;
            }

            return null;
        });
    };

    return (
        <div>
            <div className='text-medium text-size-18 margin-bottom-10'>
                <FormattedMessage id='intl-msg:smartRoute.tour.details' />
            </div>
            <div className='timeline' data-testid='tour-timeline'>
                <DeparturePanel
                    key={firstWaypoint?.id}
                    cargoAtDeparture={tour?.departure.initialCargoWeight}
                    itineraries={itineraries}
                    selectedItinerary={selectedItinerary}
                />
                {renderTimelineEvents()}
                <ArrivalPanel key={lastWaypoint?.id} itineraries={itineraries} selectedItinerary={selectedItinerary} />
            </div>
        </div>
    );
};
